import { useSelector } from "react-redux";
import { AppState } from "../../../../types/state/AppState";
import { DealsState } from "../../../../types/state/DealsState";
import {
    cleansingNoticeDocsTypeList,
    dealRegularDocsTypeList,
    DocumentType,
    getDealRegularDocumentTypes,
    getDocumentTypeText,
} from "../../../../types/amr-pipeline/enums/DocumentType";
import { DocumentsList } from "./DocumentsList";
import { DealsDefaultHeader } from "../../DealsDefaultHeader";
import { constants, roles, routes } from "../../../../constants";
import { DealsTabType } from "../../../../types/deals/DealsTabType";
import { DocumentGroup } from "../../../amrPipeline/common/documents/DocumentGroup";
import { compareDates } from "../../../../utils/compare.utils";
import { user } from "../../../../user";
import { dealsService } from "../../../../services/deals.service";
import moment from "moment";
import { getDealDocLastDate } from "../../../../utils/amr-pipeline.utils";
import { FormField } from "../../../amrPipeline/common/FormField";
import { TransactionDocument } from "../../../../types/amr-pipeline/models/TransactionDocument";

export const groupDocuments = (documentList: TransactionDocument[] = [], withEditRole: boolean) => {
    const sortedDocumentList = documentList
        .map((d) => {
            const lastDate = getDealDocLastDate(d);
            return {
                ...d,
                executionDate: moment(lastDate).format(
                    constants.formatTimeStringWithoutTimeZone
                ),
            };
        })
        .sort((a, b) => compareDates(b.executionDate, a.executionDate));

    return {
        dealDocuments: sortedDocumentList.filter((d) =>
            getDealRegularDocumentTypes().includes(d.documentType)
        ),
        cleansingNotices: sortedDocumentList.filter(
            (d) => {
                let isFormatAllowed = true;
                const withRequiredType = d.documentType === DocumentType.cleansingNoticeRefinancing 
                    || d.documentType === DocumentType.cleansingNoticeReset;

                if(!withEditRole) {
                    const splittedDocName = d.name.split('.');
                    const docFormat = splittedDocName[splittedDocName.length - 1].toLowerCase();

                    isFormatAllowed = docFormat !== 'xls' && docFormat !== 'xlsx';
                }

                return isFormatAllowed && withRequiredType
            }
        ),
        distributionReports: sortedDocumentList.filter(
            (d) => d.documentType === DocumentType.distributionReport
        ),
        monthlyReports: sortedDocumentList.filter(
            (d) => d.documentType === DocumentType.monthlyReport
        ),
        reportsExcelFiles: sortedDocumentList.filter(
            (d) => d.documentType === DocumentType.reportsExcelFiles
        ),
        otherDocuments: sortedDocumentList.filter(
            (d) => d.documentType === DocumentType.otherDocument
        ),
    };
};

export const Documents = () => {
    const { selectedDeal } = useSelector<AppState, DealsState>(
        (state) => state.deals
    );
    const isAdminOrDataEntry = user.hasRoles(roles.Administrator, roles.DataEntry);

    if (!selectedDeal) {
        return null;
    }

    const { referenceName, ticker } = selectedDeal;

    const allDocuments = isAdminOrDataEntry ? selectedDeal.documents?.dealDocuments : selectedDeal.documents?.dealDocuments?.filter(d => d.isPublic);
    const distributionReportsPattern = selectedDeal.documents?.dealDocumentPatterns.find(p => p.documentType === DocumentType.distributionReport)?.pattern;
    const monthlyReportsPattern = selectedDeal.documents?.dealDocumentPatterns.find(p => p.documentType === DocumentType.monthlyReport)?.pattern;


    const {
        dealDocuments,
        cleansingNotices,
        distributionReports,
        monthlyReports,
        reportsExcelFiles,
        otherDocuments,
    } = groupDocuments(allDocuments, isAdminOrDataEntry);

    return (
        <div className="tabs-content container-flex">
            <DealsDefaultHeader
                isEditDisabled={!ticker}
                editTooltip={ticker ? undefined : 'Please add the deal ticker before editing documents'}
                editButtonUrl={
                    isAdminOrDataEntry
                        ? routes.editDealUrl(selectedDeal.referenceName, DealsTabType.Documents)
                        : undefined
                }
            />
            <div className="tabs-data tabs-data-padding tabs-data-documents">
                <DocumentGroup
                    title="Deal Documents"
                    itemsCount={dealDocuments.length}
                    downloadAllFetch={dealsService.getDealRegularDocuments}
                    downloadAllArgs={[referenceName]}
                    fileNameForSave={`${ticker} Deal Documents.zip`}
                >
                    <DocumentsList dealReferenceName={referenceName} items={dealDocuments} documentTypes={dealRegularDocsTypeList} />
                </DocumentGroup>
                <DocumentGroup
                    title="Cleansing Notice"
                    itemsCount={cleansingNotices.length}
                    downloadAllFetch={dealsService.getCleansingNoticeDocuments}
                    downloadAllArgs={[referenceName]}
                    fileNameForSave={`${ticker} Cleansing Notice.zip`}
                >
                    <DocumentsList dealReferenceName={referenceName} items={cleansingNotices} documentTypes={cleansingNoticeDocsTypeList} />
                </DocumentGroup>
                <DocumentGroup
                    title="Distribution Reports"
                    itemsCount={distributionReports.length}
                    downloadAllFetch={dealsService.getDealDocumentsByType}
                    downloadAllArgs={[referenceName, DocumentType.distributionReport]}
                    fileNameForSave={`${ticker} ${getDocumentTypeText(DocumentType.distributionReport)}.zip`}
                >
                    <FormField title="Distribution Reports Mask" value={distributionReportsPattern ?? constants.emptyPlaceholder} />
                    <DocumentsList dealReferenceName={referenceName} items={distributionReports} />
                </DocumentGroup>
                <DocumentGroup
                    title="Monthly Reports"
                    itemsCount={monthlyReports.length}
                    downloadAllFetch={dealsService.getDealDocumentsByType}
                    downloadAllArgs={[referenceName, DocumentType.monthlyReport]}
                    fileNameForSave={`${ticker} ${getDocumentTypeText(DocumentType.monthlyReport)}.zip`}
                >
                    <FormField title="Monthly Reports Mask" value={monthlyReportsPattern ?? constants.emptyPlaceholder} />
                    <DocumentsList dealReferenceName={referenceName} items={monthlyReports} />
                </DocumentGroup>
                {isAdminOrDataEntry && (
                    <DocumentGroup
                        title="Reports Excel files"
                        itemsCount={reportsExcelFiles.length}
                        downloadAllFetch={dealsService.getDealDocumentsByType}
                        downloadAllArgs={[referenceName, DocumentType.reportsExcelFiles]}
                        fileNameForSave={`${ticker} ${getDocumentTypeText(DocumentType.reportsExcelFiles)}.zip`}
                    >
                        <DocumentsList dealReferenceName={referenceName} items={reportsExcelFiles} />
                    </DocumentGroup>
                )}
                <DocumentGroup
                    title="Other Documents"
                    itemsCount={otherDocuments.length}
                    downloadAllFetch={dealsService.getDealDocumentsByType}
                    downloadAllArgs={[referenceName, DocumentType.otherDocument]}
                    fileNameForSave={`${ticker} ${getDocumentTypeText(DocumentType.otherDocument)}.zip`}
                >
                    <DocumentsList dealReferenceName={referenceName} items={otherDocuments} />
                </DocumentGroup>
            </div>
        </div>
    );
};

